import type { RootState } from 'src/app/store';
import type { Area, Point } from 'src/data';
import { transitionTime } from 'src/constants';
import { getSelectAsset } from 'src/state';
import { store } from 'src/app/store';

import {
  audioTransitionStarted,
  audioTransitionCompleted,
} from 'src/state';

const transitionTimeMs = transitionTime * 1000;

const audioTracks: Record<string, AudioTrack> = {};

interface AudioTrack {
  audio: HTMLAudioElement,
  track: MediaElementAudioSourceNode,
  canPlay: Promise<void>,
}

// create, load, and cache the audio track
function getAudioTrack(audioId: string, audioContext: AudioContext, state: RootState): AudioTrack {
  if (audioTracks[audioId]) return audioTracks[audioId];

  const audioAsset = getSelectAsset(audioId)(state);
  const audioUrl = audioAsset.fields.file['en-US'].url;
  const audio = new Audio(audioUrl);

  const canPlay = new Promise<void>((resolve) => {
    audio.addEventListener('canplay', () => {
      resolve();
    });
  });

  audio.crossOrigin = 'anonymous';
  audio.loop = true;
  audio.load();

  const track = audioContext.createMediaElementSource(audio);
  const audioTrack = { audio, track, canPlay };
  audioTracks[audioId] = audioTrack;

  return audioTrack;
}

export function loadAudio(
  item: Point | Area,
  audioContext: AudioContext,
  state: RootState,
) {
  // checks if audio exists
  const audioField = item.fields.audio;
  if (!audioField) {
    return;
  }

  const audioId = audioField['en-US'].sys.id;
  getAudioTrack(audioId, audioContext, state);
}

export function playAudio(
  item: Point | Area,
  audioContext: AudioContext,
  state: RootState,
  gainNode: GainNode,
  instant: boolean,
) {
  // checks if audio exists
  const audioField = item.fields.audio;
  if (!audioField) {
    return;
  }

  const audioId = audioField['en-US'].sys.id;
  const { audio, track, canPlay } = getAudioTrack(audioId, audioContext, state);

  setTimeout(async () => {
    await canPlay;
    track.connect(gainNode);
    gainNode.gain.setTargetAtTime(0.95, audioContext.currentTime, 0.15);
    audio.play();
  }, instant ? 0 : transitionTimeMs / 2);

}

export function fadeOutAudio(
  item: Point | Area,
  audioContext: AudioContext,
  state: RootState,
  gainNode: GainNode,
) {
  // checks if audio exists
  const audioField = item.fields.audio;
  if (!audioField) {
    return;
  }

  const audioId = audioField['en-US'].sys.id;
  const { audio, track } = getAudioTrack(audioId, audioContext, state);

  store.dispatch(audioTransitionStarted());
  gainNode.gain.setTargetAtTime(0, audioContext.currentTime, 0.5);
  setTimeout(() => {
    track.disconnect(gainNode);
    audio.pause();
    audio.currentTime = 0;
    store.dispatch(audioTransitionCompleted());
  }, transitionTimeMs / 2);
}

